import React, { useContext } from "react";
import Link from "next/link";
import {
  type ButtonProps,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
  Button,
} from "@chakra-ui/react";
import { WrapRight } from "./WrapRight";
import { useTranslations } from "../shared/i18n/localization";
import { type Doctor, Locale } from "../shared/types";
import { CrossBulletIcon } from "./icons/CrossBulletIcon";
import { getStartCaseURL } from "../shared/helpers/urls";
import { TenantContext } from "../shared/contexts/tenantContext";
import { doctorLanguages } from "../shared/helpers/doctors";
import { sendToGoogleTagManager } from "../shared/googleTagManager";

interface DoctorChoiceButtonProps extends ButtonProps {
  doctor: Doctor;
  text?: string;
}

export const DoctorChoiceButton: React.FC<DoctorChoiceButtonProps> = ({
  doctor,
  text,
  ...buttonProps
}) => {
  const { translate, locale } = useTranslations();
  const tenantConfig = useContext(TenantContext);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const hasSameLanguage = doctorLanguages(doctor).includes(
    languageFromLocale(locale)
  );

  if (!doctor.is_available) {
    return (
      <>
        <Button
          onClick={() => {
            sendToGoogleTagManager({
              event: "absence_message_viewed",
            });
            onOpen();
          }}
          {...buttonProps}
        >
          {text ?? translate("doctorProfile.start_case")}
        </Button>
        <Modal isOpen={isOpen} onClose={onClose}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader tabIndex={0} marginTop={4}>
              <CrossBulletIcon />
              <Heading>{translate("doctor_unavailable_modal.heading")}</Heading>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>{translate("doctor_unavailable_modal.body")}</ModalBody>
            <ModalFooter>
              <WrapRight>
                <Button variant="quaternary" margin="11px" onClick={onClose}>
                  {translate("doctor_unavailable_modal.cancel")}
                </Button>
                <Link
                  href={getStartCaseURL(tenantConfig.id, {
                    lang: locale,
                  })}
                  onClick={() => {
                    sendToGoogleTagManager({
                      event: "absence_message_confirmed",
                    });
                  }}
                >
                  <Button margin="11px">
                    {translate("doctor_unavailable_modal.continue")}
                  </Button>
                </Link>
              </WrapRight>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </>
    );
  }

  if (hasSameLanguage) {
    return (
      <Link
        href={getStartCaseURL(tenantConfig.id, {
          doctor_id: parseInt(doctor.id),
          lang: locale,
        })}
      >
        <Button {...buttonProps}>
          {text ?? translate("doctorProfile.start_case")}
        </Button>
      </Link>
    );
  } else {
    return (
      <>
        <Button onClick={onOpen} {...buttonProps}>
          {text ?? translate("doctorProfile.start_case")}
        </Button>
        <Modal isOpen={isOpen} onClose={onClose}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader tabIndex={0} marginTop={4}>
              <CrossBulletIcon />
              <Heading>{translate("language_mismatch.heading")}</Heading>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>{translate("language_mismatch.text")}</ModalBody>
            <ModalFooter>
              <WrapRight>
                <Button variant="quaternary" margin="11px" onClick={onClose}>
                  {translate("language_mismatch.cancel")}
                </Button>
                <Link
                  href={getStartCaseURL(tenantConfig.id, {
                    doctor_id: parseInt(doctor.id),
                    lang: locale,
                  })}
                >
                  <Button margin="11px">
                    {translate("language_mismatch.confirm")}
                  </Button>
                </Link>
              </WrapRight>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </>
    );
  }
};

const languageFromLocale = (locale: Locale) =>
  locale === Locale["de-ch"] ? Locale.de : locale;
